import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Alert, AlertDescription } from './ui/alert';
import { projectId } from '../utils/supabase/info';
import { Trash2, Archive } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

interface User {
  id: string;
  email: string;
  name?: string;
  team: string | null;
  role: string;
  isCompanyAdmin: boolean;
  status: string;
  createdAt: string;
  companyId: string;
}

interface UserEditDialogProps {
  open: boolean;
  onClose: () => void;
  user: User | null;
  accessToken: string;
  onUpdated: () => void;
  isSystemAdmin?: boolean;
  isCompanyAdmin?: boolean;
  currentUserId?: string;
  currentUserCompanyId?: string;
}

export function UserEditDialog({
  open,
  onClose,
  user,
  accessToken,
  onUpdated,
  isSystemAdmin = false,
  isCompanyAdmin = false,
  currentUserId,
  currentUserCompanyId
}: UserEditDialogProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [team, setTeam] = useState('');
  const [role, setRole] = useState('user');
  const [status, setStatus] = useState('active');
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (open && user) {
      setEmail(user.email);
      setName(user.name || '');
      setTeam(user.team || '');
      setRole(user.role === 'companyAdmin' || user.isCompanyAdmin ? 'companyAdmin' : user.role);
      setStatus(user.status === 'approved' ? 'active' : user.status);
      setError('');
      setSuccess('');
    }
  }, [open, user]);

  const handleSave = async () => {
    if (!user) return;

    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/update-user`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            userId: user.id,
            email: email.trim(),
            name: name.trim() || null,
            team: team.trim() || null,
            role,
            status: status === 'active' ? 'approved' : status
          }),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update user');
      }

      setSuccess('User updated successfully');
      onUpdated();
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (err: any) {
      console.error('Error updating user:', err);
      setError(err.message || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async () => {
    if (!user) return;

    const confirmed = confirm(
      `Archive user ${user.email}?\n\nThis will:\n- Mark the user as archived\n- Prevent them from logging in\n- Preserve their data for audit purposes\n\nYou can restore archived users later.`
    );

    if (!confirmed) return;

    setDeleting(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/delete-user/${user.id}?soft=true`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to archive user');
      }

      setSuccess('User archived successfully');
      onUpdated();
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (err: any) {
      console.error('Error archiving user:', err);
      setError(err.message || 'Failed to archive user');
    } finally {
      setDeleting(false);
    }
  };

  const handleDelete = async () => {
    if (!user) return;

    // Confirm deletion
    const confirmed = confirm(
      `PERMANENTLY DELETE user ${user.email}?\n\nThis action CANNOT be undone and will:\n- Remove the user from the system\n- Delete their authentication credentials\n- Remove all their access permissions\n- Erase all their data\n\nConsider using "Archive" instead to preserve data.`
    );

    if (!confirmed) return;

    // Double confirmation for permanent delete
    const doubleConfirmed = confirm(
      `Final confirmation: Type the user's email to confirm permanent deletion.\n\nExpected: ${user.email}`
    );

    if (!doubleConfirmed) return;

    setDeleting(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/delete-user/${user.id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete user');
      }

      setSuccess('User permanently deleted');
      onUpdated();
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (err: any) {
      console.error('Error deleting user:', err);
      setError(err.message || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const handleRestore = async () => {
    if (!user) return;

    const confirmed = confirm(
      `Restore user ${user.email}?\n\nThis will:\n- Set their status back to Active\n- Allow them to log in again`
    );

    if (!confirmed) return;

    setDeleting(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/restore-user/${user.id}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to restore user');
      }

      setSuccess('User restored successfully');
      onUpdated();
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (err: any) {
      console.error('Error restoring user:', err);
      setError(err.message || 'Failed to restore user');
    } finally {
      setDeleting(false);
    }
  };

  const isArchivedOrDisabled = user && (user.status === 'archived' || user.status === 'disabled');

  const hasChanges = user && (
    email !== user.email ||
    name !== (user.name || '') ||
    team !== (user.team || '') ||
    role !== (user.role === 'companyAdmin' || user.isCompanyAdmin ? 'companyAdmin' : user.role) ||
    status !== (user.status === 'approved' ? 'active' : user.status)
  );

  // Determine if current user can delete this user
  const canDelete = user && user.id !== currentUserId && (
    isSystemAdmin || // System admins can delete anyone
    (isCompanyAdmin && user.companyId === currentUserCompanyId) // Company admins can delete users in their company
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
          <DialogDescription>
            Update user information and permissions
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert>
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Smith"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
            />
            {user && email.trim() !== user.email && (
              <p className="text-sm text-amber-600">Email will be updated in Supabase Auth and KV store</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="team">Team (Optional)</Label>
            <Input
              id="team"
              value={team}
              onChange={(e) => setTeam(e.target.value)}
              placeholder="Enter team name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="role">Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger id="role">
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="companyAdmin">Company Admin</SelectItem>
                {isSystemAdmin && (
                  <>
                    <SelectItem value="consultant">Consultant</SelectItem>
                    <SelectItem value="systemAdmin">System Admin</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger id="status">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="invited">Invited</SelectItem>
                <SelectItem value="disabled">Disabled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <div className="flex justify-between items-center w-full">
            <div className="flex gap-2">
              {isArchivedOrDisabled && user && user.id !== currentUserId && (
                <Button
                  variant="outline"
                  className="border-green-500 text-green-700 hover:bg-green-50"
                  onClick={handleRestore}
                  disabled={deleting}
                >
                  <Archive className="h-4 w-4 mr-2" />
                  {deleting ? 'Processing...' : 'Restore User'}
                </Button>
              )}
              {user && user.id !== currentUserId && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="destructive"
                      disabled={deleting}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      {deleting ? 'Processing...' : 'Remove User'}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuItem onClick={handleArchive} disabled={deleting}>
                      <Archive className="h-4 w-4 mr-2" />
                      <div className="flex flex-col">
                        <span>Archive User</span>
                        <span className="text-xs text-muted-foreground">Preserves data, can be restored</span>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleDelete} disabled={deleting} className="text-destructive">
                      <Trash2 className="h-4 w-4 mr-2" />
                      <div className="flex flex-col">
                        <span>Permanently Delete</span>
                        <span className="text-xs text-muted-foreground">Cannot be undone</span>
                      </div>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={loading || !hasChanges}>
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}